import { Link, useLocation } from "react-router-dom";
import { MessageSquare, Receipt, PieChart, Settings, Download, Building2, Trash2, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useClearChat } from "@/hooks/useClearChat";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const menuItems = [
  { title: "Chat", icon: MessageSquare, path: "/" },
  { title: "Receipts", icon: Receipt, path: "/receipts" },
  { title: "Summary", icon: PieChart, path: "/summary" },
  { title: "Settings", icon: Settings, path: "/settings" },
  { title: "Connect Firm", icon: Building2, path: "/connect-firm" },
  { title: "Install App", icon: Download, path: "/install" },
];

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const location = useLocation();
  const { toast } = useToast();
  const { clearChat, isClearing } = useClearChat(onClose);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "See You Soon! 👋",
      description: "Don't forget to upload those receipts when you're back!",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay - ChatGPT style blur */}
      <div 
        className="fixed inset-0 bg-background/70 backdrop-blur-md z-40 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Dropdown Menu from top - ChatGPT style */}
      <div className="fixed top-[68px] left-4 right-4 max-w-xs bg-card/98 backdrop-blur-xl border border-border/60 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] z-50 animate-dropdown overflow-hidden">
        <nav className="py-1.5">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-3.5 px-4 py-3.5 mx-2 my-0.5 rounded-xl transition-all duration-200",
                  isActive
                    ? "bg-muted text-primary font-medium"
                    : "text-foreground hover:bg-muted/60 active:bg-muted"
                )}
              >
                <item.icon className="w-[22px] h-[22px] flex-shrink-0" strokeWidth={2} />
                <span className="text-[15px]">{item.title}</span>
              </Link>
            );
          })}
        </nav>

        {/* Divider */}
        <div className="h-px bg-border/60 mx-4" />

        {/* Action buttons */}
        <div className="py-1.5">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="flex items-center gap-3.5 px-4 py-3.5 mx-2 my-0.5 rounded-xl transition-all duration-200 text-foreground hover:bg-muted/60 active:bg-muted w-[calc(100%-16px)]"
                disabled={isClearing}
              >
                <Trash2 className="w-[22px] h-[22px] flex-shrink-0" strokeWidth={2} />
                <span className="text-[15px]">{isClearing ? "Clearing..." : "Clear Chat"}</span>
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear Chat History?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete your entire conversation history. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={clearChat}>
                  Clear History
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <button
            onClick={handleLogout}
            className="flex items-center gap-3.5 px-4 py-3.5 mx-2 my-0.5 rounded-xl transition-all duration-200 text-foreground hover:bg-muted/60 active:bg-muted w-[calc(100%-16px)]"
          >
            <LogOut className="w-[22px] h-[22px] flex-shrink-0" strokeWidth={2} />
            <span className="text-[15px]">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
}
